import { Form } from 'factorial-form'
import { IObservableArray } from 'mobx'

import {
  endsOnField,
  hasTrialPeriodField,
  jobTitleField,
  salaryFields,
  trialPeriodEndDateField,
  workingHoursFields,
} from 'modules/contracts/lib/checkFieldVisibility'
import { customFieldsFormKeys, getCustomFieldKey } from 'modules/contracts/lib/customFields'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import type Employee from 'modules/employees/store/models/Employee'
import { contractVersionJobLevel } from 'modules/job_catalog/store/collections/levels'
import { jobLevelRole } from 'modules/job_catalog/store/collections/roles'
import type { PolicyAuthorizations } from 'modules/permissions/store/collections/policyAuthorizations'

const contractVersionToFormValues = (
  contractVersion: ContractVersion | undefined,
  contractVersionPolicy: ContractVersionPayrollPolicy | null
) => {
  const jobLevel = contractVersion ? contractVersionJobLevel(contractVersion) : null

  return {
    ...contractVersion?.attributes,
    effective_on: null,
    starts_on: null,
    has_trial_period: contractVersion?.attributes.has_trial_period ?? false,
    es_contract_type_id: contractVersion?.attributes.es_contract_type_id,
    es_agreement_code_id: contractVersion?.attributes.es_agreement_code_id,
    es_cotization_group: contractVersion?.attributes.es_cotization_group,
    es_contract_observations: contractVersion?.attributes.es_contract_observations,
    es_education_level_id: contractVersion?.attributes.es_education_level_id,
    es_regime_id: contractVersion?.attributes.es_regime_id,
    es_tariff_group_id: contractVersion?.attributes.es_tariff_group_id,
    es_tariff_group_name: contractVersion?.attributes.es_tariff_group_name,
    es_occupation_code_id: contractVersion?.attributes.es_occupation_code_id,
    es_classification_id: contractVersion?.attributes.es_classification_id,
    es_cno_occupation_id: contractVersion?.attributes.es_cno_occupation_id,
    es_contribution_type_id: contractVersion?.attributes.es_contribution_type_id,
    es_has_teleworking_contract: contractVersion?.attributes.es_has_teleworking_contract ?? false,
    es_professional_category_id: contractVersion?.attributes.es_professional_category_id,
    es_job_description: contractVersion?.attributes.es_job_description,
    es_working_day_type_id: contractVersion?.attributes.es_working_day_type_id,
    es_a3innuva_job_position_id: contractVersion?.attributes.es_a3innuva_job_position_id,
    fr_contract_type_id: contractVersion?.attributes.fr_contract_type_id,
    fr_employee_type: contractVersion?.attributes.fr_employee_type,
    fr_forfait_jours: contractVersion?.attributes.fr_forfait_jours ?? false,
    fr_jours_par_an: contractVersion?.attributes.fr_jours_par_an,
    fr_jours_par_an_cents: contractVersion?.attributes.fr_jours_par_an_cents,
    fr_level_id: contractVersion?.attributes.fr_level_id,
    fr_mutual_id: contractVersion?.attributes.fr_mutual_id,
    fr_professional_category_id: contractVersion?.attributes.fr_professional_category_id,
    fr_step_id: contractVersion?.attributes.fr_step_id,
    fr_work_type_id: contractVersion?.attributes.fr_work_type_id,
    fr_coefficient: contractVersion?.attributes.fr_coefficient,
    de_contract_type_id: contractVersion?.attributes.de_contract_type_id,
    pt_contract_type_id: contractVersion?.attributes.pt_contract_type_id,
    job_catalog_level_id: jobLevel?.attributes.id,
    job_catalog_role_id: jobLevel ? jobLevelRole(jobLevel)?.attributes.id : null,
    // TODO: Add the job catalog tree node uuid to the form values
    // once we have it in the contract version
    job_catalog_tree_node_uuid: '',
    payroll_policy_id: contractVersionPolicy?.attributes.payroll_policy_id,
    de_employment_type: contractVersion?.attributes.de_employment_type,
    de_flat_rate_tax: contractVersion?.attributes.de_flat_rate_tax,
    de_activity_type: contractVersion?.attributes.de_activity_type,
    de_personal_key_group_id: contractVersion?.attributes.de_personal_key_group_id,
  }
}

const customFieldsToFormValues = (
  customFields: ResourceField[],
  customValues: IObservableArray<CustomFieldValue>
) => {
  const formValues: { [key: string]: string | number } = {}

  return customFields.reduce((formValues, field) => {
    const customFieldKey = getCustomFieldKey(field)
    const customValue = customValues.find(
      (value) => value.attributes.field_id === field.attributes.field_id
    )

    if (customValue) formValues[customFieldKey] = customValue.attributes.value
    return formValues
  }, formValues)
}

export const getUpdateFlowForm = (
  contractVersion: ContractVersion | undefined,
  employee: Employee,
  contractVersionPolicy: ContractVersionPayrollPolicy | null,
  customFields: ResourceField[],
  customValues: IObservableArray<CustomFieldValue>,
  policyAuthorizations: PolicyAuthorizations
): Form => {
  const canSeeJobTitle: boolean = policyAuthorizations.for('contracts.see_job_title', employee)
  const canSeeEndDate: boolean = policyAuthorizations.for('contracts.see_end_date', employee)
  const canSeeTrialPeriod: boolean = policyAuthorizations.for(
    'contracts.see_trial_period',
    employee
  )
  const canSeeTrialPeriodEndDate: boolean = policyAuthorizations.for(
    'contracts.see_trial_period_end_date',
    employee
  )
  const canSeeGrossSalary: boolean = policyAuthorizations.for(
    'contracts.see_gross_salary',
    employee
  )
  const canSeeWorkingHours: boolean = policyAuthorizations.for(
    'contracts.see_working_hours',
    employee
  )

  return new Form(
    {
      ...contractVersionToFormValues(contractVersion, contractVersionPolicy),
      ...customFieldsToFormValues(customFields, customValues),
    },
    {
      ...jobTitleField(canSeeJobTitle),
      id: 'number',
      effective_on: 'date',
      starts_on: 'date',
      ends_on: 'date',
      bank_holiday_treatment: 'string',
      working_time_percentage_in_cents: 'number',
      annual_working_time_distribution: 'string',
      es_contract_type_id: 'number',
      es_contract_type_payroll_integration_code: 'string',
      es_contract_type_payroll_integration_name: 'string',
      es_contribution_type_id: 'number',
      es_contribution_type_payroll_integration_code: 'string',
      es_contribution_type_payroll_integration_name: 'string',
      es_cotization_group: 'string',
      es_education_level_id: 'number',
      es_agreement_code_id: 'number',
      es_agreement_code_payroll_integration_code: 'string',
      es_agreement_code_payroll_integration_name: 'string',
      es_education_level_payroll_integration_code: 'string',
      es_education_level_payroll_integration_name: 'string',
      es_cno_occupation_id: 'number',
      es_cno_occupation_payroll_integration_code: 'string',
      es_cno_occupation_payroll_integration_name: 'string',
      es_contract_observations: 'string',
      es_has_teleworking_contract: 'boolean',
      es_professional_category_id: 'number',
      es_job_description: 'string',
      es_working_day_type_id: 'number',
      es_regime_id: 'number',
      es_regime_payroll_integration_code: 'string',
      es_regime_payroll_integration_name: 'string',
      es_tariff_group_id: 'string',
      es_tariff_group_name: 'string',
      es_occupation_code_id: 'number',
      es_occupation_code_payroll_integration_code: 'string',
      es_occupation_code_payroll_integration_name: 'string',
      es_classification_id: 'number',
      es_classification_payroll_integration_code: 'string',
      es_classification_payroll_integration_name: 'string',
      es_a3innuva_job_position_id: 'number',
      es_a3innuva_job_position_payroll_integration_code: 'string',
      es_a3innuva_job_position_payroll_integration_name: 'string',
      fr_contract_type_id: 'number',
      fr_employee_type: 'string',
      fr_forfait_jours: 'boolean',
      fr_jours_par_an: 'number',
      fr_jours_par_an_cents: 'cents',
      fr_level_id: 'number',
      fr_mutual_id: 'number',
      fr_professional_category_id: 'number',
      fr_step_id: 'number',
      fr_work_type_id: 'number',
      fr_coefficient: 'string',
      de_contract_type_id: 'number',
      pt_contract_type_id: 'number',
      job_catalog_level_id: 'number',
      job_catalog_role_id: 'number',
      job_catalog_tree_node_uuid: 'string',
      payroll_policy_id: 'number',
      de_employment_type: 'number',
      de_flat_rate_tax: 'number',
      de_activity_type: 'number',
      de_personal_key_group_id: 'number',
      ...salaryFields(canSeeGrossSalary),
      ...workingHoursFields(canSeeWorkingHours),
      ...hasTrialPeriodField(canSeeTrialPeriod),
      ...trialPeriodEndDateField(canSeeTrialPeriodEndDate),
      ...endsOnField(canSeeEndDate),
      ...customFieldsFormKeys(customFields),
    }
  )
}
